/**
 * `windowGeometry` — the **React-free geometry core** for COMBAT's floating windows. Everything
 * here is a pure function over plain values so it unit-tests with no React/DOM
 * (`windowGeometry.test.ts`); the {@link useCombatWindows} registry
 * (`lib/encounter/windows.ts`) is the stateful wrapper.
 *
 * **The model.** A window's geometry is stored as **workspace percentages** — `{ x, y, w, h }`
 * as fractions of COMBAT's box — and *that* is the source of truth. The pixel rectangle the
 * {@link WindowFrame} consumes is **derived per render** against the live workspace
 * ({@link pctToPx}), so a window follows a resize instead of drifting off-screen. A live drag /
 * resize reports pixels back up, which the registry converts to percentages ({@link pxToPct}) —
 * pixels never become the stored truth.
 *
 * **Pixel chrome constants mirror {@link WindowFrame} exactly.** `pos.x`/`pos.y` are the
 * outer `.gms-window` element's top-left; the visible card sits inset by `PAD_LEFT`/
 * `PAD_TOP` (the deskmat glow). The min-size (`minW`/`minH`) is enforced **in pixels at
 * the %→px conversion**, so it wins over a small-workspace percentage that would compute
 * below it, and the position clamp keeps the card on-screen under the top inset — the same
 * bounds the frame's drag/resize gestures clamp to, so the two never disagree.
 */

/** A viewport size in CSS pixels (`window.innerWidth` / `innerHeight`). */
export type Viewport = { w: number; h: number };

/** A window's geometry as viewport fractions — the stored source of truth. */
export type PctGeometry = { x: number; y: number; w: number; h: number };

/** A window's top-left in pixels (the outer `.gms-window` element origin). */
export type WindowPos = { x: number; y: number };
/** A window's card size in pixels. */
export type WindowSize = { w: number; h: number };
/** A window's geometry in pixels — what {@link WindowFrame} consumes. */
export type PxGeometry = { pos: WindowPos; size: WindowSize };

/** A pixel minimum size, enforced at the %→px conversion. */
export type MinSize = { w: number; h: number };

// Chrome offsets — must stay in lock-step with `combatApp/WindowFrame.tsx`.
/** The deskmat pad left/right (the card is inset this far inside `.gms-window`). */
export const PAD_LEFT = 48;
/** The deskmat pad top. */
export const PAD_TOP = 48;
/** The workspace's top gutter — windows clamp, snap and dock so the card never lands above it. */
export const TOP_INSET = 34;

/** The default per-window minimum card size (`WindowFrame`'s `minW`/`minH` defaults). */
export const DEFAULT_MIN: MinSize = { w: 380, h: 180 };

// Position-clamp slack (mirrors `WindowFrame`'s drag clamp): how much of the window must stay
// reachable on each far edge so a window can never be dragged/reflowed fully off-screen.
const KEEP_RIGHT = 128;
const KEEP_BOTTOM = 108;

// A viewport fallback for non-DOM contexts (tests, SSR) so the math never divides by zero.
export const FALLBACK_VIEWPORT: Viewport = { w: 1440, h: 900 };

/** Clamp `v` into `[lo, hi]` (tolerates an inverted range on a tiny viewport via the inner min). */
export const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));

/**
 * Derive the pixel rectangle for a stored percentage geometry against `vp`. Min-size is
 * applied in pixels (so it wins over a sub-min percentage on a small viewport) and the
 * position is clamped to keep the card on-screen under the top inset — the same bounds the
 * `WindowFrame` gestures clamp to, so a render never disagrees with a drag.
 */
export function pctToPx(pct: PctGeometry, vp: Viewport, min: MinSize = DEFAULT_MIN): PxGeometry {
  const w = Math.max(min.w, pct.w * vp.w);
  const h = Math.max(min.h, pct.h * vp.h);
  const x = clamp(pct.x * vp.w, -PAD_LEFT, vp.w - KEEP_RIGHT);
  const y = clamp(pct.y * vp.h, TOP_INSET - PAD_TOP, vp.h - KEEP_BOTTOM);
  return { pos: { x, y }, size: { w, h } };
}

/** Convert a pixel geometry back to stored percentages against `vp` (the inverse codec). */
export function pxToPct(px: PxGeometry, vp: Viewport): PctGeometry {
  return {
    x: px.pos.x / vp.w,
    y: px.pos.y / vp.h,
    w: px.size.w / vp.w,
    h: px.size.h / vp.h,
  };
}

/** Convert just a moved pixel position to its percentage pair (the drag → store path). */
export function posToPct(pos: WindowPos, vp: Viewport): Pick<PctGeometry, "x" | "y"> {
  return { x: pos.x / vp.w, y: pos.y / vp.h };
}

/** Convert just a resized pixel size to its percentage pair (the resize → store path). */
export function sizeToPct(size: WindowSize, vp: Viewport): Pick<PctGeometry, "w" | "h"> {
  return { w: size.w / vp.w, h: size.h / vp.h };
}

/** Whether `g` is a structurally valid percentage geometry (all four fields finite numbers). */
export function isPctGeometry(g: unknown): g is PctGeometry {
  if (g === null || typeof g !== "object") return false;
  const r = g as Record<string, unknown>;
  return (["x", "y", "w", "h"] as const).every((k) => typeof r[k] === "number" && Number.isFinite(r[k]));
}

// ---- default-geometry / dock-spec expansion -----------------------------------------

/** A viewport edge a window can dock to. */
export type DockEdge = "left" | "right" | "top" | "bottom";

/**
 * A window's default geometry. Either an explicit **pixel** rectangle (expanded against the
 * live viewport so a first open lands pixel-faithfully and scales fluidly thereafter) or an
 * **edge-dock** sugar spec the manager expands into a pinned full-span rectangle (the
 * COMMS.app right-rail shape).
 */
export type GeometrySpec =
  | { kind: "px"; x: number; y: number; w: number; h: number }
  | { kind: "dock"; edge: DockEdge; w?: number; h?: number; margin?: number };

/** A pixel-rectangle default convenience for config authoring. */
export const px = (rect: { x: number; y: number; w: number; h: number }): GeometrySpec => ({ kind: "px", ...rect });

/**
 * An **edge-dock** default convenience for config authoring —
 * `dock({ edge: "right" })` declares "open pinned to the right edge as a full-height rail,"
 * the COMMS.app shape. The manager expands it against the live viewport on **first open**
 * only (when nothing's persisted for that id); a saved geometry overrides it thereafter, so
 * dragging a docked window away keeps it where you left it. Mirrors {@link px}; the math is
 * {@link expandDock}/{@link expandSpec}.
 */
export const dock = (spec: { edge: DockEdge; w?: number; h?: number; margin?: number }): GeometrySpec => ({
  kind: "dock",
  ...spec,
});

/** Default thickness of an edge-docked rail / panel when the spec omits `w`/`h`. */
const DOCK_THICKNESS = 380;

/**
 * Expand an edge-dock spec into a pixel rectangle pinned to that edge, spanning the
 * available cross-axis (under the top inset, inset by `margin`). The card is what's pinned,
 * so the outer-element origin backs out the deskmat pad. Used by {@link expandSpec}; kept
 * separate so the dock math is independently unit-testable.
 */
export function expandDock(
  spec: Extract<GeometrySpec, { kind: "dock" }>,
  vp: Viewport,
): PxGeometry {
  const m = spec.margin ?? 0;
  const top = TOP_INSET + m;
  const fullH = vp.h - top - m;
  const fullW = vp.w - 2 * m;
  switch (spec.edge) {
    case "right": {
      const w = spec.w ?? DOCK_THICKNESS;
      const cardLeft = vp.w - m - w;
      return { pos: { x: cardLeft - PAD_LEFT, y: top - PAD_TOP }, size: { w, h: fullH } };
    }
    case "left": {
      const w = spec.w ?? DOCK_THICKNESS;
      return { pos: { x: m - PAD_LEFT, y: top - PAD_TOP }, size: { w, h: fullH } };
    }
    case "top": {
      const h = spec.h ?? DOCK_THICKNESS;
      return { pos: { x: m - PAD_LEFT, y: top - PAD_TOP }, size: { w: fullW, h } };
    }
    case "bottom": {
      const h = spec.h ?? DOCK_THICKNESS;
      const cardTop = vp.h - m - h;
      return { pos: { x: m - PAD_LEFT, y: cardTop - PAD_TOP }, size: { w: fullW, h } };
    }
  }
}

/** Expand a default-geometry spec into stored percentages against `vp`. */
export function expandSpec(spec: GeometrySpec, vp: Viewport): PctGeometry {
  if (spec.kind === "px") {
    return pxToPct({ pos: { x: spec.x, y: spec.y }, size: { w: spec.w, h: spec.h } }, vp);
  }
  return pxToPct(expandDock(spec, vp), vp);
}

// ---- snap-during-drag ---------------------------------------------------------------

/**
 * A pixel rectangle in **screen (card) coordinates** — `{x,y}` is the visible card's top-left
 * and `{w,h}` its size. This is the space snapping + dock zones reason in: the manager reads
 * neighbour card rects straight off the DOM ({@link Element.getBoundingClientRect}) and builds
 * the dragged card's rect from its outer-element position by adding the `PAD_LEFT`/`PAD_TOP`
 * inset, so every rect here is directly comparable to a viewport edge.
 */
export type Rect = { x: number; y: number; w: number; h: number };

/** Default snap pull distance (px) — a dragged edge within this of a target line sticks to it. */
export const SNAP_THRESHOLD = 8;

/**
 * The smallest signed offset that pulls one of `edges` onto one of `lines`, within `threshold`
 * (0 when nothing is close enough). Used per-axis by {@link resolveSnap}: the moving edges are
 * the dragged rect's two edges on that axis, the lines are the viewport + neighbour edges.
 */
function bestSnap(edges: number[], lines: number[], threshold: number): number {
  let delta = 0;
  let bestDist = threshold; // accept distance <= threshold; prefer the closest line
  for (const edge of edges) {
    for (const line of lines) {
      const d = line - edge;
      const dist = Math.abs(d);
      if (dist <= bestDist) {
        bestDist = dist;
        delta = d;
      }
    }
  }
  return delta;
}

/**
 * Resolve **snap-during-drag**: pull the `dragged` card rect's edges onto the nearest viewport
 * edge or neighbour-rect edge within {@link SNAP_THRESHOLD}, **independently per axis**, and
 * return the snapped rect (same size — only `x`/`y` move). The viewport lines are the workspace
 * bounds (left `0`, right `vp.w`, top `TOP_INSET` so a window snugs under the top inset, bottom
 * `vp.h`); each neighbour contributes its left+right (X) and top+bottom (Y) so windows align
 * edge-to-edge AND abut. Pure — {@link useCombatWindows} passes card rects + the live viewport.
 */
export function resolveSnap(dragged: Rect, neighbors: Rect[], vp: Viewport): Rect {
  const xLines = [0, vp.w, ...neighbors.flatMap((n) => [n.x, n.x + n.w])];
  const yLines = [TOP_INSET, vp.h, ...neighbors.flatMap((n) => [n.y, n.y + n.h])];
  const dx = bestSnap([dragged.x, dragged.x + dragged.w], xLines, SNAP_THRESHOLD);
  const dy = bestSnap([dragged.y, dragged.y + dragged.h], yLines, SNAP_THRESHOLD);
  return { x: dragged.x + dx, y: dragged.y + dy, w: dragged.w, h: dragged.h };
}

// ---- dock zones — halves + maximize -------------------------------------------------

/** A drag-to-edge dock target: the left/right half of the workspace, or full-screen maximize. */
export type DockZone = "left" | "right" | "maximize";

/** Default edge hot-zone thickness (px) — a drag cursor within this of a viewport edge hits it. */
export const DOCK_ZONE_HIT = 12;

/**
 * Resolve the dock zone a drag **cursor** is over (screen coordinates), or `null`. The TOP edge
 * maximizes and **wins the top corners** (checked first); the LEFT / RIGHT edges fill that half.
 * Quadrants are deferred (presets serve the narrow-rail need). Pure — the manager feeds the live
 * cursor; {@link zoneGeometry} turns the result into a rectangle.
 */
export function resolveDockZone(
  cursor: { x: number; y: number },
  vp: Viewport,
  hit = DOCK_ZONE_HIT,
): DockZone | null {
  if (cursor.y <= TOP_INSET + hit) return "maximize";
  if (cursor.x <= hit) return "left";
  if (cursor.x >= vp.w - hit) return "right";
  return null;
}

/**
 * The pixel geometry a dock zone fills — left/right half or the full workspace, pinned under the
 * top inset. Delegates to {@link expandDock} so the pinned-rail math lives in exactly one place
 * (a half is just a half-width edge dock; maximize is a full-height top dock). Pure.
 */
export function zoneGeometry(zone: DockZone, vp: Viewport): PxGeometry {
  switch (zone) {
    case "left":
      return expandDock({ kind: "dock", edge: "left", w: vp.w / 2 }, vp);
    case "right":
      return expandDock({ kind: "dock", edge: "right", w: vp.w / 2 }, vp);
    case "maximize":
      return expandDock({ kind: "dock", edge: "top", h: vp.h - TOP_INSET }, vp);
  }
}

// ---- full-perimeter resize ----------------------------------------------------------

/**
 * Which window edge / corner a resize gesture grips. The 1–2 letter code names the moving
 * edge(s): `n`/`s`/`e`/`w` for the four sides, the corners (`ne`, `nw`, `se`, `sw`) combining
 * one X edge and one Y edge. The letters are disjoint, so {@link resizeGeometry} reads each axis
 * by a simple `handle.includes(...)`.
 */
export type ResizeHandle = "n" | "s" | "e" | "w" | "ne" | "nw" | "se" | "sw";

/**
 * Apply a resize drag (`delta` = cursor offset from where the grip was pressed, against `base` =
 * the geometry captured at press) for one {@link ResizeHandle}, **purely**. Each axis is handled
 * independently:
 *
 * - **east / south** edges grow the size only — the origin (the opposite, west/north edge) is
 *   pinned, so `x`/`y` never move.
 * - **west / north** edges **move the origin**: the gripped edge follows the cursor while the
 *   opposite edge (`base` right / bottom) stays put, so the size changes *and* `x`/`y` shift.
 *
 * The min-size clamp **pins the opposite edge** in every case: shrinking an east/south edge past
 * `min` just floors the size (origin already fixed); shrinking a west/north edge past `min` floors
 * the size *and* recomputes the origin off the pinned opposite edge, so the window never crosses
 * itself. Corners combine the two axes. The SE handle reduces to the old single-grip behaviour
 * (size only, origin pinned), so non-manager callers keep working unchanged.
 */
export function resizeGeometry(args: {
  handle: ResizeHandle;
  base: PxGeometry;
  /** Cursor offset from where the grip was pressed. */
  delta: { dx: number; dy: number };
  min?: MinSize;
}): PxGeometry {
  const { handle, base, delta, min = DEFAULT_MIN } = args;
  let { x } = base.pos;
  let { w } = base.size;
  if (handle.includes("e")) {
    w = Math.max(min.w, base.size.w + delta.dx); // right edge moves, origin pinned
  } else if (handle.includes("w")) {
    const right = base.pos.x + base.size.w; // pin the right edge
    w = Math.max(min.w, base.size.w - delta.dx);
    x = right - w; // origin (left edge) follows, clamped off the pinned right
  }

  let { y } = base.pos;
  let { h } = base.size;
  if (handle.includes("s")) {
    h = Math.max(min.h, base.size.h + delta.dy); // bottom edge moves, origin pinned
  } else if (handle.includes("n")) {
    const bottom = base.pos.y + base.size.h; // pin the bottom edge
    h = Math.max(min.h, base.size.h - delta.dy);
    y = bottom - h; // origin (top edge) follows, clamped off the pinned bottom
  }

  return { pos: { x, y }, size: { w, h } };
}

/**
 * **Snap-during-resize**: pull a resized card rect's *moving* edges onto the nearest viewport /
 * neighbour line within {@link SNAP_THRESHOLD}, sharing {@link resolveSnap}'s line engine
 * (`bestSnap` over the same viewport + neighbour edges) so a resized edge sticks exactly like a
 * dragged one. Unlike {@link resolveSnap} (which moves the whole rect), this snaps **only the
 * gripped edge** and adjusts size — the opposite edge stays pinned, re-clamped to `min`. Pure:
 * {@link useCombatWindows} feeds the card rect (built from the just-resized geometry), the
 * neighbour rects snapshotted at resize start, and the live viewport.
 */
export function resolveResizeSnap(args: {
  handle: ResizeHandle;
  rect: Rect;
  neighbors: Rect[];
  vp: Viewport;
  min?: MinSize;
}): Rect {
  const { handle, rect, neighbors, vp, min = DEFAULT_MIN } = args;
  const xLines = [0, vp.w, ...neighbors.flatMap((n) => [n.x, n.x + n.w])];
  const yLines = [TOP_INSET, vp.h, ...neighbors.flatMap((n) => [n.y, n.y + n.h])];
  let { x, y, w, h } = rect;
  if (handle.includes("e")) {
    const d = bestSnap([x + w], xLines, SNAP_THRESHOLD); // snap the right edge; left pinned
    w = Math.max(min.w, w + d);
  } else if (handle.includes("w")) {
    const right = x + w;
    const d = bestSnap([x], xLines, SNAP_THRESHOLD); // snap the left edge; right pinned
    w = Math.max(min.w, right - (x + d));
    x = right - w;
  }
  if (handle.includes("s")) {
    const d = bestSnap([y + h], yLines, SNAP_THRESHOLD); // snap the bottom edge; top pinned
    h = Math.max(min.h, h + d);
  } else if (handle.includes("n")) {
    const bottom = y + h;
    const d = bestSnap([y], yLines, SNAP_THRESHOLD); // snap the top edge; bottom pinned
    h = Math.max(min.h, bottom - (y + d));
    y = bottom - h;
  }
  return { x, y, w, h };
}
